import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import { Tabs, DatePicker, Form, Input, Modal, Select, Skeleton, Space, Spin, notification } from 'antd'
import { useDispatch, useSelector } from 'react-redux'
import dayjs from '@plugins/local-dayjs'

import {
  getExamApplyTemplate,
  getExamDetail,
  getExamDictData,
  getExamineeApplyInfo,
  getExamPositionList, getPositionInfo, getTreeData, getTreeDict, updateExamineeInfo,
  uploadFile,
  uploadPersonImage
} from '../../common/request'
import ResponseCode from '../../common/response-code'
import { updateDictInfo } from '../../store/slice/dict-slice'
import { FieldTypeMap } from '../../common/common'
import { parseTableHeaderItem } from '../../common/tableHeaderConstants'
import { getUid } from '../../plugins/utils'
import TableFormField from './TableFormField'
import FileFormField from './FileFormField'
import RegionFormField from './RegionFormField'
import TreeDataFormField from './TreeDataFormField'
import { mailValidate, phoneValidate } from '../../plugins/validates'
import eventBus from '../../plugins/events'

const POSITION_SELECT_FIELDS = ['siteCode', 'siteName', 'unitCode', 'unitName', 'positionCode', 'positionId', 'positionName', 'positionNumber']

const clonePlainValue = (value) => {
  if (value == null || value === '') {
    return value
  }
  if (typeof value !== 'object') {
    return value
  }
  if (dayjs.isDayjs(value)) {
    return value
  }
  try {
    return JSON.parse(JSON.stringify(value))
  } catch {
    return value
  }
}

const getApplyFieldFormValue = (field, rawValue) => {
  if (rawValue == null || rawValue === '') {
    return rawValue ?? ''
  }
  if (field.fieldType === '5') {
    return dayjs(new Date(rawValue))
  }
  if (field.fieldType === '7') {
    return Array.isArray(rawValue)
      ? rawValue.map(row => ({
        ...clonePlainValue(row),
        id: getUid()
      }))
      : []
  }
  return clonePlainValue(rawValue)
}

const EditExamineeInfoModal = forwardRef((props, ref) => {
  const [form] = Form.useForm()
  const [fileForm] = Form.useForm()
  const dispatch = useDispatch()
  const [notifyApi, contextHolder] = notification.useNotification()
  const dictInfo = useSelector(state => state.dict.dictInfo)
  const [modalVisible, setModalVisible] = useState(false)
  const [loading, setLoading] = useState(false)
  const [initDataLoading, setInitDataLoading] = useState(false)
  const [templateField, setTemplateField] = useState([])
  const [applyInfo, setApplyInfo] = useState({})
  const [formItems, setFormItems] = useState([])
  const [positionFormFields, setPositionFormFields] = useState([])
  const [formFileItems, setFormFileItems] = useState([])
  const [siteOptions, setSiteOptions] = useState([])
  const [examInfo, setExamInfo] = useState({})
  const [positionList, setPositionList] = useState([])
  const [currentSitePosition, setCurrentSitePosition] = useState([])
  const [activeTab, setActiveTab] = useState('applyField')
  const [currentSiteUnitOptions, setCurrentSiteUnitOptions] = useState([])
  const systemFields = ['applyDateTime', 'reviewer', 'reviewDate']
  const [positionDetailInfo, setPositionDetailInfo] = useState({})

  useImperativeHandle(ref, () => {
    return {
      showModal
    }
  })

  useEffect(() => {
    if (modalVisible) {
      getExamineeApplyInfoAction()
    }
  }, [modalVisible])

  useEffect(() => {
    setSiteOptions(examInfo.applySiteList?.map(item => {
      return {
        ...item,
        label: item.name,
        value: item.applySiteCode
      }
    }))
  }, [examInfo])

  useEffect(() => {
    if (modalVisible) {
      filterUnitOptions(props.examineeItem.siteCode)
      filterPositionOptions(props.examineeItem.unitCode)
    }
  }, [applyInfo, positionFormFields, modalVisible])

  useEffect(() => {
    responseTransform()
  }, [templateField])

  useEffect(() => {
    if (!modalVisible || !applyInfo.applyInfo || formFileItems.length === 0) {
      return
    }

    const fileValues = {}
    formFileItems.forEach(field => {
      fileValues[field.fieldCode] = field.fieldType === '6'
        ? applyInfo.applyInfo[field.fieldCode]
        : applyInfo.applyFiles?.[field.fieldCode]
    })
    fileForm.setFieldsValue(fileValues)
  }, [modalVisible, formFileItems, applyInfo])

  useEffect(() => {
    if (!modalVisible || !applyInfo.applyInfo || formItems.length === 0) {
      return
    }

    const applyValues = {}
    formItems.filter(field => !POSITION_SELECT_FIELDS.includes(field.fieldCode)).forEach(field => {
      applyValues[field.fieldCode] = getApplyFieldFormValue(field, applyInfo.applyInfo[field.fieldCode])
    })
    form.setFieldsValue(applyValues)

    if (applyInfo.positionId) {
      getPositionInfoAction(applyInfo.positionId)
    }
  }, [modalVisible, formItems, applyInfo])

  const getPositionInfoAction = (positionId) => {
    if (!positionId) {
      return
    }

    getPositionInfo({ id: positionId }).then(res => {
      if (res.code === ResponseCode.success) {
        const positionData = clonePlainValue(res.data) || {}
        setPositionDetailInfo(positionData)

        const positionValues = {
          siteCode: positionData.siteCode,
          siteName: positionData.siteName,
          positionId: positionData._id,
          positionCode: positionData.positionCode,
          positionNumber: positionData.positionNumber,
          unitName: positionData.unitName,
          unitCode: positionData.unitCode
        }

        positionFormFields.filter(field => !POSITION_SELECT_FIELDS.includes(field.fieldCode)).forEach(field => {
          positionValues[field.fieldCode] = positionData[field.fieldCode]
        })

        form.setFieldsValue(positionValues)
      }
    })
  }

  const responseTransform = () => {
    if (templateField.fields) {
      setFormItems(templateField.fields.filter(item => {
        preLoadDictInfo(item)

        return item.fieldType !== '1' && item.fieldType !== '6'
      }))

      setPositionFormFields(templateField.fields.filter(field => {
        return field.fieldFromType === '1'
      }))

      setFormFileItems([].concat(templateField.fields.filter(item => {
        return item.fieldType === '6'
      }), templateField.fileFields || []))
    }

    setInitDataLoading(false)
  }

  const preLoadDictInfo = (item) => {
    if (item.fieldJoinSelect) {
      if (item.fieldType === FieldTypeMap.select) {
        getDictInfo(item.fieldJoinSelect)
      } else {
        getTreeDataAction(item.fieldJoinSelect)
      }
    }
  }

  const getDictInfo = (fieldJoinSelect) => {
    if (!dictInfo[fieldJoinSelect]) {
      getExamDictData({ dictCode: fieldJoinSelect }).then(res => {
        if (res.code === ResponseCode.success) {
          dispatch(updateDictInfo({ dictKey: fieldJoinSelect, dictValue: res.data }))
        } else {
          Modal.error({
            title: '错误',
            centered: true,
            content: res.msg
          })
        }
      }).catch((err) => {
        console.error(err)
        Modal.error({
          title: '网络错误',
          centered: true,
          content: '查询字段类型字典失败'
        })
      })
    }
  }

  const getTreeDataAction = (fieldJoinSelect) => {
    if (!dictInfo[fieldJoinSelect]) {
      getTreeDict({ dictCode: fieldJoinSelect }).then(res => {
        if (res.code === ResponseCode.success) {
          dispatch(updateDictInfo({ dictKey: fieldJoinSelect, dictValue: res.data }))
        } else {
          Modal.error({
            title: '错误',
            centered: true,
            content: res.msg
          })
        }
      }).catch((err) => {
        console.error(err)
        Modal.error({
          title: '网络错误',
          centered: true,
          content: '查询字段类型字典失败'
        })
      })
    }
  }

  const showModal = () => {
    setModalVisible(true)
  }

  const hideModal = () => {
    setActiveTab('applyField')
    setModalVisible(false)
    setPositionDetailInfo({})
    form.resetFields()
    fileForm.resetFields()
  }

  const getExamineeApplyInfoAction = () => {
    setInitDataLoading(true)
    const params = {
      applyInfoId: props.examineeItem.applyInfoId,
      examId: props.examineeItem.examId,
      positionNumber: props.examineeItem.positionNumber,
      userId: props.examineeItem.userId
    }

    const positionListParams = {
      pageNum: 1,
      pageSize: 9999,
      examId: props.examineeItem.examId
    }

    Promise.all([
      getExamineeApplyInfo(params),
      getExamApplyTemplate({ examId: props.examineeItem.examId }),
      getExamDetail({ id: props.examineeItem.examId }),
      getExamPositionList(positionListParams)
    ]).then(([applyInfoRes, templateFieldRes, examInfoRes, positionListRes]) => {
      if (applyInfoRes.code !== ResponseCode.success) {
        Modal.error({
          title: '错误',
          content: '查询考生报名信息失败'
        })
        return
      }

      if (templateFieldRes.code !== ResponseCode.success) {
        Modal.error({
          title: '错误',
          content: '查询考试报名字段失败'
        })
        return
      }

      if (examInfoRes.code !== ResponseCode.success) {
        Modal.error({
          title: '错误',
          content: '查询考试详情失败',
          centered: true
        })
        return
      }

      if (positionListRes.code !== ResponseCode.success) {
        Modal.error({
          title: '错误',
          content: '查询考试职位信息失败',
          centered: true
        })
        return
      }

      setTemplateField(templateFieldRes.data)
      setApplyInfo(applyInfoRes.data)
      setExamInfo(examInfoRes.data)
      setPositionList(positionListRes.data?.elements.map(item => {
        return {
          label: `${ item.positionCode }_${ item.positionName }`,
          value: item._id,
          ...item
        }
      }))
    }).catch(err => {
      setInitDataLoading(false)
      Modal.error({
        title: '错误',
        content: '网络错误，查询考生报名信息失败'
      })
    })
  }

  const generateExamineeApplyFormItems = (fieldList) => {
    return fieldList.map(item => {
      return systemFields.includes(item.fieldAuto)
        ? <Form.Item
          noStyle
          key={ item.fieldCode }
          name={ item.fieldCode }
          label={ item.fieldName }
        ></Form.Item>
        : <Form.Item
          key={ item.fieldCode }
          label={ item.fieldName }
          name={ item.fieldCode }
          rules={ getFormItemRule(item) }
        >
          { generateFormComponent(item) }
        </Form.Item>
    })
  }

  const generateOtherPositionFormItems = (fieldList) => {
    return fieldList.filter(item => {
      return !['unitName', 'unitCode', 'positionName', 'positionCode'].includes(item.fieldCode)
    }).map(item => {
      return (<Form.Item
          key={ item.fieldCode }
          label={ item.fieldName }
          name={ item.fieldCode }
          rules={ getFormItemRule(item) }
        >
          <Input disabled style={ { width: '560px' } }></Input>
        </Form.Item>)
    })
  }

  const generateFormComponent = (field) => {
    let component

    switch (field.fieldType) {
      case FieldTypeMap.input:
      case FieldTypeMap.phone:
      case FieldTypeMap.mail:
        component = <Input
          placeholder={ `请输入` }
          style={ { width: '560px' } }
        ></Input>
        break
      case FieldTypeMap.textarea:
        component = <Input.TextArea
          placeholder={ `请输入` }
          style={ { width: '560px' } }
        ></Input.TextArea>
        break
      case FieldTypeMap.select:
        component = <Select
          options={ dictInfo[field.fieldJoinSelect]?.map(dict => {
            return {
              label: dict.dictLabel,
              value: dict.dictValue
            }
          }) }
          style={ { width: '560px' } }
          placeholder={ `请选择` }
        >
        </Select>
        break
      case FieldTypeMap.dateRangePicker:
        component = <DatePicker
          style={ { width: '560px' } }
          format={ 'YYYY-MM-DD' }
        ></DatePicker>
        break
      case FieldTypeMap.table:
        const columnsData = field.selectContent.split('|').map(item => {
          const parsed = parseTableHeaderItem(item)
          return {
            title: parsed.name,
            key: parsed.name,
            dataIndex: parsed.name,
            type: parsed.type,
            fieldJoinSelect: parsed.content,
            description: parsed.description,
            fieldNumber: parsed.fieldNumber
          }
        })

        component = (
          <TableFormField
            columnsData={ columnsData }
            fieldItem={ templateField.fields?.find(item => {
              return item.fieldCode === field.fieldCode
            }) }
          ></TableFormField>
        )
        break
      case FieldTypeMap.treeData:
        component = <TreeDataFormField
          fieldJoinSelect={ field.fieldJoinSelect }
        ></TreeDataFormField>
        break
      case FieldTypeMap.cascader:
        component = <RegionFormField></RegionFormField>
        break
    }

    return component
  }

  const fileUploading = () => {
    setLoading(true)
  }

  const fileUploaded = () => {
    setLoading(false)
  }

  const filterUnitOptions = (siteCode) => {
    let obj = {}
    positionList.filter(positionItem => {
      return positionItem.siteCode === siteCode
    }).forEach(positionItem => {
      obj[positionItem.unitCode] = {
        label: `${ positionItem.unitCode }_${ positionItem.unitName }`,
        value: positionItem.unitCode,
        siteCode: positionItem.siteCode
      }
    })

    setCurrentSiteUnitOptions(Object.values(obj))
  }

  const currentSiteChange = (siteCode, option) => {
    const resetValues = {
      siteName: option?.label ?? '',
      siteCode,
      positionId: null,
      positionNumber: null
    }
    positionFormFields.forEach(field => {
      resetValues[field.fieldCode] = null
    })
    form.setFieldsValue(resetValues)
    filterUnitOptions(siteCode)
  }

  const filterPositionOptions = (unitCode) => {
    setCurrentSitePosition(positionList.filter(positionItem => {
      return positionItem.unitCode === unitCode
    }).map(positionItem => ({
      label: positionItem.label,
      value: positionItem.value
    })))
  }

  const unitCodeChange = (unitCode, option) => {
    const resetValues = {
      unitName: option?.label?.split('_')[1] ?? '',
      positionId: null,
      positionNumber: null
    }
    positionFormFields.forEach(field => {
      if (!['unitCode', 'unitName'].includes(field.fieldCode)) {
        resetValues[field.fieldCode] = null
      }
    })
    form.setFieldsValue(resetValues)
    filterPositionOptions(unitCode)
  }

  const generateFileFormContent = (field) => {
    switch (field.fieldType) {
      case '6':
        return <FileFormField
          fileName={ '个人照片' }
          fieldType={ field.fieldType }
          uploadApi={ uploadPersonImage }
          fileUploading={ fileUploading }
          fileUploaded={ fileUploaded }
        ></FileFormField>
        break
      case '11':
        return <FileFormField
          fieldItem={ field }
          fileName={ field.fieldName }
          fieldType={ field.fieldType }
          uploadApi={ uploadFile }
          fileUploading={ fileUploading }
          fileUploaded={ fileUploaded }
        ></FileFormField>
        break
    }
  }

  const getFormItemRule = (field) => {
    let rule

    switch (field.fieldType) {
      case FieldTypeMap.input:
        rule = [{ required: true, min: 1 }]
        break
      case FieldTypeMap.phone:
        rule = [{ validator: phoneValidate }]
        break
      case FieldTypeMap.mail:
        rule = [{ validator: mailValidate }]
        break
      default:
        rule = [{ required: true }]
    }

    return field.fieldIsNotBlank === '1' ? rule : []
  }

  const generateFileList = (list) => {
    if (list.length > 0) {
      return (
        <div>
          <Form
            form={ fileForm }
            className={ 'mt-2' }
            labelCol={ {
              span: 4
            } }
          >
            {
              formFileItems.map((item, index) => {
                return (
                  <Form.Item
                    label={ item.fieldName }
                    name={ item.fieldCode }
                    key={ index }
                  >
                    { generateFileFormContent(item) }
                  </Form.Item>
                )
              })
            }
          </Form>
        </div>
      )
    } else {
      return <></>
    }
  }

  const positionIdChange = (positionId) => {
    const position = positionList.find(item => item._id === positionId || item.value === positionId)
    if (!position) {
      return
    }

    const positionValues = {
      positionNumber: position.positionNumber
    }
    positionFormFields.forEach(field => {
      if (!['unitCode', 'unitName'].includes(field.fieldCode)) {
        positionValues[field.fieldCode] = clonePlainValue(position[field.fieldCode])
      }
    })
    form.setFieldsValue(positionValues)
  }

  const tabItems = [
    {
      label: '报名字段',
      key: 'applyField',
      forceRender: true,
      children: (<>
        <Form
          form={ form }
          className={ 'mt-3' }
          labelCol={ {
            span: 6
          } }

          wrapperCol={ {
            span: 16
          } }
        >
          <Form.Item
            label={ '报名点' }
            name={ 'siteCode' }
            rules={ [{ required: true, message: '请选择报名点' }] }
          >
            <Select
              options={ siteOptions }
              style={ { width: '560px' } }
              onChange={ currentSiteChange }
            ></Select>
          </Form.Item>
          <Form.Item
            name={ 'siteName' }
            hidden
          >
            <Input></Input>
          </Form.Item>
          <Form.Item
            noStyle
            name={ 'unitName' }
          ></Form.Item>
          <Form.Item
            label={ '报考单位' }
            name={ 'unitCode' }
            rules={ [{ required: true, message: '请选择报考单位' }] }
          >
            <Select
              options={ currentSiteUnitOptions }
              style={ { width: '560px' } }
              placeholder={ '请选择报考单位' }
              onChange={ unitCodeChange }
            ></Select>
          </Form.Item>
          <Form.Item
            label={ '报考职位' }
            name={ 'positionId' }
            rules={ [{ required: true, message: '请选择报考职位' }] }
          >
            <Select
              options={ currentSitePosition }
              style={ { width: '560px' } }
              placeholder={ '请选择职位' }
              onChange={ positionIdChange }
            ></Select>
          </Form.Item>
          <Form.Item
            name={ 'positionNumber' }
            hidden
          >
            <Input></Input>
          </Form.Item>
          <Form.Item
            name={ 'positionName' }
            hidden
          >
            <Input></Input>
          </Form.Item>
          <Form.Item
            name={ 'positionCode' }
            hidden
          >
            <Input></Input>
          </Form.Item>
          { generateOtherPositionFormItems(positionFormFields) }
          { generateExamineeApplyFormItems(formItems) }
        </Form>
      </>)
    },
    {
      label: '附件',
      key: 'fileField',
      forceRender: true,
      children: (<>
        { generateFileList(formFileItems) }
      </>)
    }
  ]

  const activeTabChange = (key) => {
    setActiveTab(key)
  }

  const initLoading = (initLoading) => {
    if (initLoading) {
      return (
        <Spin spinning={ true }>
          <Space className={ 'mt-4 mb-3 d-flex justify-content-center' }>
            <Skeleton.Button active={ true }/>
            <Skeleton.Avatar active={ true }/>
            <Skeleton.Input active={ true }/>
          </Space>
          <Space className={ 'mb-3 d-flex justify-content-center' }>
            <Skeleton.Button active={ true }/>
            <Skeleton.Avatar active={ true }/>
            <Skeleton.Input active={ true }/>
          </Space>
          <Space className={ 'mb-3 d-flex justify-content-center' }>
            <Skeleton.Button active={ true }/>
            <Skeleton.Avatar active={ true }/>
            <Skeleton.Input active={ true }/>
          </Space>
          <Space className={ 'mb-3 d-flex justify-content-center' }>
            <Skeleton.Button active={ true }/>
            <Skeleton.Avatar active={ true }/>
            <Skeleton.Input active={ true }/>
          </Space>
          <Space className={ 'mb-3 d-flex justify-content-center' }>
            <Skeleton.Button active={ true }/>
            <Skeleton.Avatar active={ true }/>
            <Skeleton.Input active={ true }/>
          </Space>
        </Spin>
      )
    } else {
      return (
        <Spin spinning={ loading }>
          <Tabs
            items={ tabItems }
            activeKey={ activeTab }
            onChange={ activeTabChange }
          ></Tabs>
        </Spin>
      )
    }
  }

  const onOk = () => {
    Promise.all([
      form.validateFields(),
      fileForm.validateFields()
    ]).then(([applyFields, fileFormFields]) => {
      const params = {
        ...applyInfo,
        positionId: applyFields['positionId'],
        positionNumber: applyFields['positionNumber'],
        applyInfo: {
          siteCode: applyFields['siteCode'],
          siteName: applyFields['siteName']
        },
        applyFiles: {}
      }
      templateField.fields?.forEach(field => {
        switch (field.fieldType) {
          case '1':
            params.applyInfo[field.fieldCode] = applyFields[field.fieldCode]
            break
          case '5':
            params.applyInfo[field.fieldCode] = applyFields[field.fieldCode]
              ? dayjs(applyFields[field.fieldCode]).format(applyInfo.applyInfo[field.fieldCode].indexOf(' ') > -1 ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD').toString()
              : ''
            break
          case '6':
            params.applyInfo[field.fieldCode] = fileFormFields[field.fieldCode]
            break
          default:
            params.applyInfo[field.fieldCode] = applyFields[field.fieldCode]
            if (field.fieldAuto) {
              params[field.fieldAuto] = applyFields[field.fieldCode]
            }
        }
      })

      templateField.fileFields?.forEach(field => {
        params.applyFiles[field.fieldCode] = fileFormFields[field.fieldCode]
      })

      setLoading(true)

      updateExamineeInfo(params).then(res => {
        setLoading(false)
        if (res.code === ResponseCode.success) {
          notifyApi.success({
            message: '操作成功'
          })
          eventBus.emit(
            'reloadExamineeApplyInfo',
            props.examineeItem.applyInfoId || props.examineeItem.id
          )
          hideModal()
          props.insertSuccess && props.insertSuccess()
        } else {
          Modal.error({
            title: '错误',
            content: res.msg
          })
        }
      }).catch(err => {

      })
    }).catch((err) => {
      if (formFileItems.find(item => {
        return item.fieldCode === err.errorFields[0].name[0]
      })) {
        Modal.error({
          title: '错误',
          content: err.errorFields[0].errors[0]
        })
        setActiveTab('fileField')

        return
      }

      if (formItems.find(item => {
        return item.fieldCode === err.errorFields[0].name[0]
      })) {
        Modal.error({
          title: '错误',
          content: err.errorFields[0].errors[0]
        })
        setActiveTab('applyField')

        return
      }
    })
  }

  return (
    <div ref={ ref }>
      { contextHolder }

      <Modal
        width={ 1000 }
        title={ '修改考生报名信息' }
        open={ modalVisible }
        onOk={ onOk }
        okText={ '保存' }
        confirmLoading={ loading }
        destroyOnHidden
        onCancel={ hideModal }
      >
        { initLoading(initDataLoading) }
      </Modal>
    </div>
  )
})

export default EditExamineeInfoModal
